import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { ConnectionType, IAudioBridgeResponse, IConnectionDetailsDTO, IDeleteConnectionRequestParams, KnovioLabel, WebinarAutoRecording } from '../../../../../../api/connections.dao';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { FormViewModelItemState, FormVmValueValidationStatus, ToggleValues, TSelectOptionsValue } from '../../../../../../infrastructure/form-viewmodel-controller';
import { ConnectionVmProvider, IConnectionFormViewModelController } from '../../../../../../modules/connections/event-connection-vm.provider';
import {
    appendConnectionType,
    renderAdditionalKnovioConnectionFields,
    renderAdditionalOtherConnectionFields,
    renderZoomFields
} from '../../../../../../modules/connections/decorators';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { configOverrides, getConfigItemByKey } from '../../../../../../modules/connections/event-connection-config';
import { ConnectionFields } from '../../../../../../modules/connections/config';
import { KnovioLinkViewModel } from './knovio-link.viewmodel';
import { OneclickStartLivestreamLinkViewModel } from './oneclick-start-livestream-link.viewmodel';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { isTrackingFieldEnabled, TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { MixPanelTrackingConnectionMapper } from './mixpanel-tracking-connection-form.mapper';
import { IScheduledZoomMeetingDetailsDTO, IZoomDAO, ZoomMeetingType } from '../../../../../../api/zoom.dao';
import { ApprovalType } from '../_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import {
    additionalTrakingForConnection,
    attachKnovioPodiumTrackFields,
    meetingAudioBridgeRequestDetails,
    meetingWebinarRequestDetails
} from '../../../../../../infrastructure/utils/connections.utils';
import { generatePodiumEmbedCode } from '../../../../../../infrastructure/utils/knovio.utils';
import { IOrganizationsForSpeakersSync, SyncSpeakersVM } from './_sync-speakers/sync-speakers.viewmodel';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { IButtonOption } from '../../../../../global/options-button/options-button';
import KnovioPreviewLinkPopoverVM, { KnovioPreviewSourceType } from '../../knovio-preview-link-popover/knovio-preview-link-popover.viewmodel';
import { IDeleteConnectionDetails } from '../_connection-delete-confirm-popover';
import { IBlueprintsDAO } from '../../../../../../api/blueprints/blueprints.dao';
import { AudioBridgeConnectionField } from './connection-form-body/audio-bridge-connection-form/audio-bridge-connection.form-config';
import { ConnectionVersion, NONE } from '../../../../../../constants/common.constants';

export enum FormInputType {
    TextInput = 'input',
    Textarea = 'textarea',
    Select = 'select',
    Checkbox = 'checkbox',
    RichTextInput = 'RichTextEditor',
    Toggle = 'toggle',
}

type FieldProps = {
    fieldType: FormInputType;
    options?: (string | number | void | TSelectOptionsValue)[];
    label: string;
    readOnly?: boolean ;
    toggleValues?: ToggleValues;
    toggleInfoMessage?: string | JSX.Element | Record<string, JSX.Element>;
};
export type MeetingApiParams = {
    eventId: number;
    accountId: number;
    meetingId?: number | string;
    roomId?: number | string;
    connectionId?: number;
    knovioPresentationId?: number;
};
export type FormFieldsProps = FormViewModelItemState & FormVmValueValidationStatus & FieldProps;
export type InitialZoomWebinarToggleValues = Record<string, string>;

export const CONNECTION_TYPE = 'Connection Type';
export const CONNECTION_VERSION = 'connectionVersion';
const IS_KNOVIO_TYPE = `Connection Type ${KnovioLabel.OEStream}`;
const IS_OTHER_TYPE = 'Connection Type Other';
const WEBINAR_CONNECTION_STATUS = 'webinarConnectionStatus';
const AUDIO_BRIDGE_CONNECTION_STATUS = 'audioBridgeConnectionStatus';
export const zoomWebinarTypes = [ConnectionType.Webinar, ConnectionType.Zoom];
class ConnectionFormVM {
    private static readonly fieldTypeMap: Record<string, string[]> = {
        [FormInputType.Checkbox as string]: [ ConnectionFields.LIVE_STREAM_ENABLED ],
        [FormInputType.Select as string]: [ ConnectionFields.DIAL_FOR_VS, ConnectionFields.MEETING_UNIQUE_REFERENCE ],
        [FormInputType.Textarea as string]: [ ConnectionFields.MEETING_INVITATION, ConnectionFields.CONNECTION_NOTES, ConnectionFields.ZOOM_DESCRIPTION ],
        [FormInputType.RichTextInput as string]: [ ConnectionFields.MEETING_LOCATION, ConnectionFields.DETAILS_PLACEHOLDER_TEXT ],
        [FormInputType.Toggle as string]: [
            ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED,
            ConnectionFields.AUTO_REGISTRATION_ENABLED,
            ConnectionFields.REGISTRATION_ENABLED,
            ConnectionFields.AUTOMATICALLY_APPROVAL,
            ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL,
            ConnectionFields.PASSPORT_REPLAY,
            ConnectionFields.PUBLISH_CONNECTION,
            ConnectionFields.AUTOMATICALLY_RECORD
        ]
    };

    static getFieldTypeByName(name: string): FormInputType {
        for (const key of [...Object.keys(ConnectionFormVM.fieldTypeMap)]) {
            if (ConnectionFormVM.fieldTypeMap[key].includes(name)) return key as FormInputType;
        }

        return FormInputType.TextInput;
    }


    static getFieldPropsByName = (name: string, connectionType: ConnectionType): FieldProps => ({
        label: getConfigItemByKey(name, configOverrides[connectionType])?.label || getConfigItemByKey(name)?.label || '',
        options: getConfigItemByKey(name)?.valueOptions ?? [],
        toggleValues: getConfigItemByKey(name)?.toggleValues ?? {},
        toggleInfoMessage: getConfigItemByKey(name)?.toggleInfoMessage ?? '',
        fieldType: ConnectionFormVM.getFieldTypeByName(name)
    });

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    private vm: IConnectionFormViewModelController;
    public knovioLinkVM?: KnovioLinkViewModel;
    public oneclickStartLivestreamLinkVM?: OneclickStartLivestreamLinkViewModel;
    public syncSpeakersVM: SyncSpeakersVM;
    public knovioPreviewLinkPopoverVM?: KnovioPreviewLinkPopoverVM;
    @mobx.observable
    private saving: boolean;
    @mobx.observable
    public connectionTemplateName: string | undefined = '';
    @mobx.observable
    public knovioConnectionStatus: boolean = null;
    @mobx.observable
    public loadWebinarDetails: boolean;
    @mobx.observable
    public [WEBINAR_CONNECTION_STATUS]: boolean = null;
    @mobx.observable
    public webinarDetails: IConnectionDetailsDTO;
    private connectionIdLastValue: string = '';
    @mobx.observable
    private initialZoomWebinarTogglesChanged = false;
    @mobx.observable
    public loadAudioBridgeDetails: boolean;
    @mobx.observable
    public [AUDIO_BRIDGE_CONNECTION_STATUS]: boolean = null;

    @mobx.observable
    public initialValues: IConnectionDetailsDTO;
    constructor(
        private readonly version: ConnectionVersion,
        private readonly meetingConnectionDetails: IConnectionDetailsDTO,
        public readonly meetingApiParams: MeetingApiParams,
        private readonly executeSubmitApiCall: (connectionDetails: IConnectionDetailsDTO) => void,
        public onCloseZoomConnection: () => void,
        public deleteConnection?: (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) => Promise<void>,
        private readonly getKnovioConnectionDetails?: (connectionDetails: IConnectionDetailsDTO) => Promise<IConnectionDetailsDTO>,
        public readonly isMeetingConnectionsForm?: boolean,
        public readonly showZoomAutoRegAndAutoSyncToggles?: boolean,
        public readonly isClonedConnection?: boolean,
        public readonly shouldShowKnovioReplayToggle?: boolean,
        public readonly zoomMeetingType?: string,
        private readonly organizationsForSpeakersSync?: IOrganizationsForSpeakersSync,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {
        this.saving = false;
    }

    public shouldShowConnectionTemplateName(): boolean {
        return hasPermission(Permission.BLUEPRINTS_TAB) && !!this.initialValues.blueprintId;
    }

    @mobx.action
    public initialize() {
        this.vm = ConnectionVmProvider.createViewModel(this.meetingConnectionDetails);
        this.syncSpeakersVM = new SyncSpeakersVM(this.organizationsForSpeakersSync, this.meetingApiParams);
        this.initialValues = this.meetingConnectionDetails;
        this.initConnectionTemplateName();

        if (this[IS_KNOVIO_TYPE]) {
            this.knovioLinkVM = new KnovioLinkViewModel(this.vm);
            this.oneclickStartLivestreamLinkVM = new OneclickStartLivestreamLinkViewModel(this.vm);
            this.syncPodiumFields(this.meetingConnectionDetails, !this.meetingConnectionDetails.podiumEmbedCode);

            if (this.isEdit()) {
                this.initKnovioConnection();

                this.knovioPreviewLinkPopoverVM = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Connection);
                this.knovioPreviewLinkPopoverVM.setConnectionInfo({
                    accountId: this.meetingApiParams.accountId,
                    eventId: this.meetingApiParams.eventId,
                    meetingId: this.meetingApiParams.meetingId,
                    meetingConnectionDetailsId: this.meetingConnectionDetails.meetingConnectionId
                });
            }
        }
        if (!this.isEdit() && zoomWebinarTypes.includes(this.meetingConnectionDetails.type)) {
            const createdViaAPI = this.meetingConnectionDetails.approvalType !== undefined;
            if (createdViaAPI) this.initialZoomWebinarTogglesChanged = true;
        }
    }

    public getKnovioConnectionOptions = (): IButtonOption[] => {
        const syncSpeakersPermission: boolean = hasPermission(Permission.SYNC_SPEAKERS_TO_KNOVIO);
        const speakersLength: boolean = !!this.syncSpeakersVM.getEnabledSpeakers().length;
        const showSyncSpeakersOption: boolean = syncSpeakersPermission && this.knovioConnectionStatus && speakersLength;
        return [
            showSyncSpeakersOption && {
                id: 'oe.connections.syncSpeakers.button',
                label: 'Sync Speakers',
                action: this.syncSpeakersVM.openSyncSpeakersModal,
            },
        ].filter(Boolean);
    };

    public shouldShowOptionsButton = (): boolean => {
        const connectionManagmentPermission: boolean = hasPermission(Permission.CONNECTIONS_MANAGEMENT);
        return connectionManagmentPermission && !!this.getConnectionFormDataVM().meetingConnectionId;
    };

    @mobx.action
    private initKnovioConnection = async (): Promise<void> => {
        try {
            const response = await this.getKnovioConnectionDetails(this.meetingConnectionDetails);
            const { knovioPresentationId, embedCodeId, meetingConnectionId } = response;
            this.syncPodiumFields(response, true, response.knovioEditMetadata?.podiumAccessCode);

            this.syncSpeakersVM.setEmbedCodeId(embedCodeId);
            this.syncSpeakersVM.setMeetingConnectionId(meetingConnectionId);
                 mobx.runInAction(() => {
                    if (knovioPresentationId) {
                        this.knovioConnectionStatus = true;
                    } else {
                        this.knovioConnectionStatus = false;
                    }
                });

        } catch (e) {
            mobx.runInAction(() => {
                this.knovioConnectionStatus = false;
            });
            this.handleApiException(e);
        }
    };

    private syncPodiumFields(
        connectionDetails: IConnectionDetailsDTO,
        shouldRegenerateEmbedCode = false,
        accessCodeOverride?: string
    ): void {
        const podiumLink = (this.vm.getValue(ConnectionFields.PODIUM_LINK) as string) || connectionDetails.podiumLink;
        const podiumAccessCode = accessCodeOverride || connectionDetails.podiumAccessCode;

        if (accessCodeOverride) {
            this.vm.setValue(ConnectionFields.PODIUM_ACCESS_CODE, accessCodeOverride);
        }

        if (shouldRegenerateEmbedCode && podiumLink && podiumAccessCode) {
            this.vm.setValue(
                ConnectionFields.PODIUM_EMBED_CODE,
                generatePodiumEmbedCode(podiumLink, podiumAccessCode)
            );
        }
    }

    public handleApiException = (error: Error): void => {
        if (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
        }
    };

    private trackConnectionMixPanel = (response: IConnectionDetailsDTO) => {
        const { eventId, meetingId, roomId } = this.meetingApiParams;
        const audioProviderName = this.vm.getValue(AudioBridgeConnectionField.AUDIO_PROVIDER_NAME)?.toString() || '';
        const trackingFields = {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': eventId.toString(),
            ...MixPanelTrackingConnectionMapper.connectionDataToTrackingEvent(
                response,
                this.isEdit() && this.meetingConnectionDetails,
                audioProviderName
            ),
        };
        trackingFields['Unique Reference'] = !!response.meetingUniqueReference
            ? response.meetingUniqueReference.toString()
            : NONE;

        if (meetingId) {
            trackingFields['Meeting ID'] = meetingId.toString();
        }
        if (roomId) {
            trackingFields['Room ID'] = roomId.toString();
        }
        if (
            !this.isEdit()
            && zoomWebinarTypes.includes(response.type)
            && this.initialZoomWebinarTogglesChanged
        ) {
            additionalTrakingForConnection(response, trackingFields);
        }
        if (response.type === ConnectionType.Knovio) {
            attachKnovioPodiumTrackFields(response, trackingFields);
        }
        if ((response.type === ConnectionType.Knovio || response.type === ConnectionType.Other) && this.shouldShowKnovioReplayToggle) {
            trackingFields['Passport Replay'] = isTrackingFieldEnabled(response.passportReplayEnabled);
        }
        if (response.type === ConnectionType.Zoom) {
            if (this.zoomMeetingType === ZoomMeetingType.SCHEDULED_MEETING) {
                trackingFields['Zoom Meeting Type'] = 'Scheduled';
            } else {
                trackingFields['Zoom Meeting Type'] = 'Recurring - No Fixed Time';
            }
        }
        const eventName = this.isEdit() ? TRACKING_EVENTS.EDIT_CONNECTION : TRACKING_EVENTS.NEW_CONNECTION;
        this.trackingService.get().track(eventName, trackingFields);
    };

    private assignFieldProps = (item: FormViewModelItemState): FieldProps => ({
        ...item,
        ...ConnectionFormVM.getFieldPropsByName(item.name, this[CONNECTION_TYPE])
    });

    private assignValidationState = (item: FormViewModelItemState): FormVmValueValidationStatus => ({
        ...item,
        ...this.vm.getValueValidationState(item.name)
    });

    private getVmFormFieldProps() {
        return this.vm.getVmFields()
            .map(value => [this.assignFieldProps, this.assignValidationState]
                .reduce((_val, mapper) => mapper(_val), value as FormFieldsProps)
            ) as FormFieldsProps[]
        ;
    }

    public get [CONNECTION_TYPE](): ConnectionType {
        return this.vm.type;
    }
    private get [IS_KNOVIO_TYPE](): boolean {
        return this[CONNECTION_TYPE] === ConnectionType.Knovio;
    }

    private get [IS_OTHER_TYPE](): boolean {
        return this[CONNECTION_TYPE] === ConnectionType.Other;
    }

    public get [CONNECTION_VERSION](): ConnectionVersion {
        return this.version;
    }

    private get isValid(): boolean {
        return this[IS_KNOVIO_TYPE]
            ? this.knovioLinkVM.isValid && this.oneclickStartLivestreamLinkVM.isValid && this.vm.isVmValid()
            : this.vm.isVmValid();
    }

    @appendConnectionType(CONNECTION_TYPE)
    @renderAdditionalOtherConnectionFields(IS_OTHER_TYPE)
    @renderAdditionalKnovioConnectionFields(IS_KNOVIO_TYPE)
    @renderZoomFields(WEBINAR_CONNECTION_STATUS, CONNECTION_VERSION)
    public getFormFields(): FormFieldsProps[] {
        return this.getVmFormFieldProps();
    }

    public isEdit() {
        return this.vm.isExistingConnection() && !this.meetingConnectionDetails.draft;
    }

    public getConnectionFormDataVM() {
        return this.vm;
    }

    @mobx.action
    public formSubmitAction = async () => {
        if (!this.isValid || this.loadWebinarDetails) {
            this.vm.setVmTouched();
            return;
        }
        this.saving = true;
        const webinarToggleValues = [
            ConnectionFields.REGISTRATION_ENABLED,
            ConnectionFields.AUTOMATICALLY_APPROVAL,
            ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL
        ];
        const { autoRegistrationEnabled, autoSyncEnabled } = this.meetingConnectionDetails;
        const connectionDetails = this.vm.toApiDTO();

        try {
            const data = {
                ...connectionDetails,
                autoRegistrationEnabled: this[WEBINAR_CONNECTION_STATUS] ? connectionDetails.autoRegistrationEnabled : autoRegistrationEnabled,
                autoSyncEnabled: this[WEBINAR_CONNECTION_STATUS] ? connectionDetails.autoSyncEnabled : autoSyncEnabled
            };
            const approvalType = !this.vm.getValue(ConnectionFields.REGISTRATION_ENABLED)
                ? ApprovalType.NOT_REQUIRED
                : data.approvalType === false
                ? ApprovalType.MANUALLY
                : ApprovalType.AUTOMATICALLY
            ;
            data.approvalType = approvalType;
            connectionDetails.approvalType = approvalType;
            delete data.registrationEnabled;
            if (!this[WEBINAR_CONNECTION_STATUS]) {
                webinarToggleValues.forEach(toggle => delete data[toggle]);
            }

            if (!this.isEdit() && this.meetingConnectionDetails?.connectionId && zoomWebinarTypes.includes(this.vm.type)) {
                data.registrantsConfirmationEmail = this.meetingConnectionDetails.registrantsConfirmationEmail;
                data.approvalType = this.meetingConnectionDetails.approvalType;
            }
            if (!this[WEBINAR_CONNECTION_STATUS]) {
                connectionDetails.registrationEnabled =
                    this.meetingConnectionDetails.approvalType === ApprovalType.NOT_REQUIRED
                    ? false
                    : true
                ;
            }
            if (this.initialZoomWebinarTogglesChanged && !this[WEBINAR_CONNECTION_STATUS]) {
                connectionDetails.approvalType = this.meetingConnectionDetails.approvalType;
                connectionDetails.registrantsConfirmationEmail = this.meetingConnectionDetails.registrantsConfirmationEmail;
            }
            if ((connectionDetails.type === ConnectionType.Knovio || connectionDetails.type === ConnectionType.Other) && !this.shouldShowKnovioReplayToggle) {
                delete data.passportReplayEnabled;
                delete connectionDetails.passportReplayEnabled;
            }
            if (this[IS_KNOVIO_TYPE] && data.draft) {
                delete data.draft;
            }

            if (connectionDetails.type === ConnectionType.Webinar) {
                this.updateAutoRecordingInWebinarDTO(data);
            }

            if (connectionDetails.type === ConnectionType.AudioBridge) {
                this.updateRecordCallsInAudioBridgeDTO(data);
            }

            await this.executeSubmitApiCall(data);
            this.trackConnectionMixPanel(connectionDetails);
            return true;
        } catch (error) {
            this.handleApiException(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    public isSaving() {
        return this.saving;
    }

    public isAutomaticallyCreatedZoomConnection = (): boolean => this.vm.type === ConnectionType.Zoom &&
                                             !!this.meetingConnectionDetails.connectionId?.length && !this.isClonedConnection;

    public getWebinarDetails = async (connectionFieldName: string, webinarOrMeetingId: string): Promise<void> => {
        if (!zoomWebinarTypes.includes(this.vm.type)) return;
        if (connectionFieldName !== ConnectionFields.CONNECTION_ID) return;
        if (!webinarOrMeetingId || webinarOrMeetingId === this.connectionIdLastValue) return;
        if (!this.isMeetingConnectionsForm || this.isEdit() || !this.showZoomAutoRegAndAutoSyncToggles) return;
        mobx.runInAction(() => {
            this.initialZoomWebinarTogglesChanged = false;
            this.loadWebinarDetails = true;
            this[WEBINAR_CONNECTION_STATUS] = null;
        });
        try {
            this.connectionIdLastValue = webinarOrMeetingId;
            const zoomDAOHandler = meetingWebinarRequestDetails();
            const currentWebinarDetails = await zoomDAOHandler[this.vm.type]({
                accountId: this.meetingApiParams.accountId,
                eventId: this.meetingApiParams.eventId,
                meetingId: webinarOrMeetingId
            });
            this.setWebinarValues(currentWebinarDetails);
            mobx.runInAction(() => {
                this[WEBINAR_CONNECTION_STATUS] = true;
            });
        } catch (e) {
            mobx.runInAction(() => {
                this[WEBINAR_CONNECTION_STATUS] = false;
            });
            this.setWebinarValues();
        } finally {
            mobx.runInAction(() => {
                this.loadWebinarDetails = false;
            });
        }
    };

    @mobx.action
    public setWebinarValues(connection?: IScheduledZoomMeetingDetailsDTO): void {
        const fields = this.getFormFields();
        fields.forEach(field => {
            switch (field.name) {
                case ConnectionFields.ALTERNATIVE_LINK:
                    this.vm.setValue(field.name, connection?.joinUrl ?? '');
                    break;
                case ConnectionFields.CONNECTION_LINK:
                    this.vm.setValue(field.name, connection?.joinUrl ?? '');
                    break;
                case ConnectionFields.HOST_LINK:
                    this.vm.setValue(field.name, connection?.startUrl ?? '');
                    break;
                case ConnectionFields.CONNECTION_PASSWORD:
                    this.vm.setValue(field.name, connection?.password ?? '');
                    break;
                case ConnectionFields.CONNECTION_ACCOUNT:
                    this.vm.setValue(field.name, connection?.zoomUser?.email ?? '');
                    break;
                case ConnectionFields.SIP:
                    this.vm.setValue(field.name, connection?.sip ?? '');
                    break;
                case ConnectionFields.CONNECTION_HOST_KEY:
                    this.vm.setValue(field.name, connection?.zoomUser?.hostKey ?? '');
                    break;
                case ConnectionFields.CONNECTION_TOPIC:
                    this.vm.setValue(field.name, connection?.topic ?? '');
                    break;
                case ConnectionFields.REGISTRATION_LINK:
                    this.vm.setValue(field.name, connection?.registrationUrl ?? '');
                    break;
                default:
                    break;
            }
        });
        if (connection) {
            this.setTogglesDefaultValues(
                connection.approvalType === ApprovalType.NOT_REQUIRED ? false : true,
                connection.approvalType === ApprovalType.AUTOMATICALLY ? true : false,
                connection.registrantsConfirmationEmail,
                this[CONNECTION_TYPE] === ConnectionType.Webinar ? connection.autoRecording : null
            );
            this.initialZoomWebinarTogglesChanged = true;
        }
    }

    public getAudioBridgeDetails = async (connectionFieldName: string, conferenceId: string): Promise<void> => {
        if (connectionFieldName !== AudioBridgeConnectionField.CONFERENCE_ID) return;
        if (!conferenceId || conferenceId === this.connectionIdLastValue) return;
        if (!this.isMeetingConnectionsForm || this.isEdit()) return;
        mobx.runInAction(() => {
            this.loadAudioBridgeDetails = true;
            this[AUDIO_BRIDGE_CONNECTION_STATUS] = null;
        });
        try {
            this.connectionIdLastValue = conferenceId;
            const currentAudioBridgeDetails = await meetingAudioBridgeRequestDetails({audioBridgeId: conferenceId});
            this.setAudioBridgeValues(currentAudioBridgeDetails);
            mobx.runInAction(() => {
                this[AUDIO_BRIDGE_CONNECTION_STATUS] = true;
            });
        } catch (e) {
            mobx.runInAction(() => {
                this[AUDIO_BRIDGE_CONNECTION_STATUS] = false;
            });
            this.setAudioBridgeValues();
        } finally {
            mobx.runInAction(() => {
                this.loadAudioBridgeDetails = false;
            });
        }
    };

    @mobx.action
    public setAudioBridgeValues(connection?: IAudioBridgeResponse): void {
        const fields = this.getFormFields();
        fields.forEach(field => {
            switch (field.name) {
                case AudioBridgeConnectionField.AUDIO_BRIDGE_NAME:
                    this.vm.setValue(field.name, connection?.name ?? '');
                    break;
                case AudioBridgeConnectionField.AUDIO_BRIDGE_ID:
                    this.vm.setValue(field.name, connection?.audioBridgeID ?? '');
                    break;
                case AudioBridgeConnectionField.AUDIO_PROVIDER_ID:
                    this.vm.setValue(field.name, connection?.audioProviderId ?? '');
                    break;
                case AudioBridgeConnectionField.PROVIDER:
                    this.vm.setValue(field.name, connection?.provider ?? '');
                    break;
                case AudioBridgeConnectionField.OPERATOR_PIN:
                    this.vm.setValue(field.name, connection?.operatorPin ?? '');
                    break;
                default:
                    break;
            }
        });
    }

    @mobx.action
    private setTogglesDefaultValues(
        registrationEnabled: boolean,
        approval: boolean,
        emailConfirmation: boolean,
        autoRecording: WebinarAutoRecording | null
    ): void {
        const toggleValues = {
            [ConnectionFields.AUTO_REGISTRATION_ENABLED]: false,
            [ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED]: false,
            [ConnectionFields.REGISTRATION_ENABLED]: registrationEnabled,
            [ConnectionFields.AUTOMATICALLY_APPROVAL]: approval,
            [ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL]: emailConfirmation,
        };

        /* we support autoRecording only for Zoom Webinars */
        if (autoRecording) {
            toggleValues[ConnectionFields.AUTOMATICALLY_RECORD] = autoRecording === WebinarAutoRecording.Cloud;
        }

        Object.keys(toggleValues).forEach(toggle => this.vm.setValue(toggle, toggleValues[toggle]));
    }

    @mobx.action
    public toggleClickHandler(formFieldName: string): void {
        const currentToggleValue = this.vm.getValue(formFieldName);
        this.vm.setValue(formFieldName, !currentToggleValue);
        switch (formFieldName) {
            case ConnectionFields.AUTO_REGISTRATION_ENABLED:
                this.checkEmailNotification();
                if (this.vm.getValue(ConnectionFields.AUTO_REGISTRATION_ENABLED)) this.enableSyncMeetingAttendeesWithZoom();
                break;
            case ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED:
                if (!currentToggleValue) {
                    this.enableSyncMeetingAttendeesWithZoom();
                }
                break;
            default:
                break;
        }
    }

    @mobx.action
    public checkEmailNotification = (): void => {
        if (
            this.vm.getValue(ConnectionFields.AUTO_REGISTRATION_ENABLED)
            && this.vm.getValue(ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL)
        ) {
            this.toggleEmailNotification();
        }
    };

    @mobx.action
    public toggleEmailNotification = (): void => {
        this.vm.setValue(
            ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL,
            !this.vm.getValue(ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL)
        );
    };

    @mobx.action
    private enableSyncMeetingAttendeesWithZoom(): void {
        this.vm.setValue(ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL, false);
        this.vm.setValue(ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED, true);
        this.vm.setValue(ConnectionFields.AUTOMATICALLY_APPROVAL, true);
        this.vm.setValue(ConnectionFields.REGISTRATION_ENABLED, true);
    }

    @mobx.action
    public checkDisabledStatus(fieldName: string): boolean {
        switch (fieldName) {
            case ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED:
                return (this.vm.getValue(ConnectionFields.AUTO_REGISTRATION_ENABLED) as boolean);
            case ConnectionFields.REGISTRATION_ENABLED:
                return (this.vm.getValue(ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED) as boolean);
            case ConnectionFields.AUTOMATICALLY_APPROVAL:
                return (this.vm.getValue(ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED) as boolean);
            case ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL:
                return (this.vm.getValue(ConnectionFields.AUTO_REGISTRATION_ENABLED) as boolean)
                    || (this.vm.getValue(ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED) as boolean);
            default:
                return false;
        }
    }

    @mobx.action
    private initConnectionTemplateName = async (): Promise<void> => {
        if (!this.shouldShowConnectionTemplateName()) return;

        try {
            const connections = await this.blueprintsDAO.getConnectionTemplateList({ accountId: this.meetingApiParams.accountId });
            const connection = connections.find(cn => cn.blueprintId === this.initialValues.blueprintId);

            mobx.runInAction(() => {
                this.connectionTemplateName = connection?.name;
            });
        } catch (e) {
            ConnectionFormVM.handleApiException(e);
        }
    };

    // convert Webinar autoRecording value from Boolean to WebinarAutoRecording or remove before sending DTO
    private updateAutoRecordingInWebinarDTO(data: IConnectionDetailsDTO): void {
        const autoRecordingValue = data[ConnectionFields.AUTOMATICALLY_RECORD];

        if (typeof autoRecordingValue === 'boolean') {
            if (!this[WEBINAR_CONNECTION_STATUS]) {
                delete data[ConnectionFields.AUTOMATICALLY_RECORD];
            } else {
                data[ConnectionFields.AUTOMATICALLY_RECORD] = autoRecordingValue ? WebinarAutoRecording.Cloud : WebinarAutoRecording.None;
            }
        }
    }

    private updateRecordCallsInAudioBridgeDTO(data: IConnectionDetailsDTO): void {
        const payload = data as IConnectionDetailsDTO & { recordCalls?: boolean; automaticallyRecordEnabled?: boolean };
        payload.recordCalls = payload.automaticallyRecordEnabled;
        delete payload.automaticallyRecordEnabled;
    }
}

export { ConnectionFormVM };
